import type { User, ApiResponse, Organization } from '../types/milestone.types'
import { cognitoConfig } from '../config/cognito'
import config from '../config/environments'
import { stackService } from './stackService'
import { tokenRefreshService } from './tokenRefreshService'
import { idleDetectionService } from './idleDetectionService'

/**
 * Authentication Service Layer
 * Direct AWS SDK integration with Cognito - no Amplify dependency!
 * Supports both direct SDK auth and Cognito Hosted UI
 */
export class AuthService {
  private static instance: AuthService

  private constructor() {
    this.initialize()
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  /**
   * Initialize the authentication service
   */
  private initialize(): void {
    // Cookie-based auth - no client or session restoration needed
    // Cognito client only needed for Hosted UI URL building (handled by cognitoConfig)
  }

  /**
   * Check if authentication service is ready to use
   */
  public isReady(): boolean {
    return cognitoConfig.isReady()
  }

  /**
   * Sign in with Cognito Hosted UI (OAuth2/OIDC flow)
   * Redirects user to AWS Cognito's built-in login page
   *
   * NEW FLOW: Cognito redirects to backend Lambda which sets httpOnly cookies
   */
  public async signInWithHostedUI(): Promise<ApiResponse<void>> {
    try {
      console.log('🚀 Starting Hosted UI authentication...')

      // Use backend OAuth callback endpoint as redirect_uri (sets httpOnly cookies)
      const backendCallbackUrl = `${config.authApiBaseUrl}/auth/callback`
      console.log('🔗 Backend callback URL:', backendCallbackUrl)

      const hostedUIUrl = cognitoConfig.buildHostedUIUrl(backendCallbackUrl)
      console.log('🔗 Built Hosted UI URL:', hostedUIUrl)
      
      if (!hostedUIUrl) {
        console.error('❌ Hosted UI URL is null - configuration issue')
        return {
          success: false,
          data: undefined,
          message: 'Hosted UI not configured yet',
          errors: [{ 
            code: 'HOSTED_UI_NOT_READY', 
            message: 'Boss needs to configure Cognito Hosted UI domain: your-company.auth.us-east-2.amazoncognito.com' 
          }]
        }
      }

      // Generate state parameter for backend OAuth callback
      // Backend expects: base64url-encoded JSON {csrf: string, returnUrl?: string}
      const csrfToken = this.generateState()

      // Never return to /login or /logout after login - always go to root
      // Include query string to preserve URL parameters like ?id=FB-12345
      const currentPath = window.location.pathname
      const currentSearch = window.location.search
      const fullPath = currentPath + currentSearch
      const excludedPaths = ['/login', '/logout', '/oauth/callback']
      const returnUrl = excludedPaths.includes(currentPath) ? '/' : (fullPath || '/')

      const stateObj = {
        csrf: csrfToken,
        returnUrl: returnUrl,
        origin: window.location.origin  // For localhost dev support
      }

      // Encode as URL-safe base64 (backend uses urlsafe_b64decode)
      const stateJson = JSON.stringify(stateObj)
      const stateBase64 = btoa(stateJson)
        .replace(/\+/g, '-')  // Convert to URL-safe
        .replace(/\//g, '_')
        // Keep padding - Python's urlsafe_b64decode expects it

      const finalUrl = `${hostedUIUrl}&state=${encodeURIComponent(stateBase64)}`

      console.log('🔐 Generated state object:', stateObj)
      console.log('🔐 Encoded state:', stateBase64)
      console.log('🎯 Final redirect URL:', finalUrl)
      console.log('🚀 Redirecting to Cognito Hosted UI...')

      // Redirect to Cognito Hosted UI
      window.location.href = finalUrl
      
      return {
        success: true,
        data: undefined,
        message: 'Redirecting to Cognito Hosted UI'
      }
    } catch (error: any) {
      console.error('❌ Hosted UI authentication error:', error)
      return {
        success: false,
        data: undefined,
        message: 'Failed to initiate hosted UI flow',
        errors: [{
          code: error.name || 'HOSTED_UI_ERROR',
          message: error.message || 'Failed to redirect to Cognito Hosted UI'
        }]
      }
    }
  }

  /**
   * Handle OAuth callback after user returns from Cognito Hosted UI
   * NEW: Backend handles token exchange and sets httpOnly cookies (Phase 4.1)
   */
  public async handleOAuthCallback(): Promise<ApiResponse<User>> {
    try {
      console.log('🔄 Starting OAuth callback processing...')
      const urlParams = new URLSearchParams(window.location.search)
      const success = urlParams.get('success')
      const error = urlParams.get('error')

      console.log('📋 OAuth callback parameters:', { success, error })

      // Check for backend OAuth callback success (cookies already set)
      if (success === 'true') {
        console.log('✅ Backend OAuth callback successful - cookies already set')
        console.log('🧹 Cleaning up callback URL...')
        window.history.replaceState({}, document.title, window.location.pathname)

        // Return placeholder user - actual user info will be fetched via /me endpoint
        return {
          success: true,
          data: {
            id: 'pending',
            email: 'pending',
            name: 'Loading...',
            organizations: [],
            currentOrganization: undefined
          },
          message: 'Authentication successful - fetching user info...'
        }
      }

      // Check for OAuth errors from backend
      if (error) {
        console.error('❌ OAuth error from backend:', error)
        return {
          success: false,
          data: null as any,
          message: `Authentication failed: ${error}`,
          errors: [{ code: 'AUTH_ERROR', message: error }]
        }
      }

      // No success or error parameter - invalid callback
      console.error('❌ Invalid OAuth callback - missing success parameter')
      return {
        success: false,
        data: null as any,
        message: 'Invalid OAuth callback',
        errors: [{ code: 'INVALID_CALLBACK', message: 'Missing success parameter' }]
      }
    } catch (error: any) {
      console.error('❌ OAuth callback handling failed:', error)
      return {
        success: false,
        data: null as any,
        message: 'OAuth callback handling failed',
        errors: [{
          code: error.name || 'OAUTH_CALLBACK_ERROR',
          message: error.message || 'Failed to process OAuth callback'
        }]
      }
    }
  }

  /**
   * Check if current URL contains OAuth callback parameters
   * NEW: Backend handles OAuth callback and redirects with success=true (Phase 4.1)
   */
  public isOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search)
    const hasError = urlParams.has('error')
    const hasSuccess = urlParams.has('success')
    console.log('🔍 isOAuthCallback check:', { hasError, hasSuccess, search: window.location.search })
    return hasError || hasSuccess
  }

  /**
   * Sign in with email and password using SRP (Secure Remote Password)
   * @deprecated Use signInWithHostedUI() instead for cookie-based authentication
   *
   * Direct sign-in is not compatible with httpOnly cookie authentication.
   * Use Cognito Hosted UI for secure cookie-based auth.
   */
  public async signIn(_email: string, _password: string): Promise<ApiResponse<User>> {
    console.warn('⚠️  signIn() is deprecated. Use signInWithHostedUI() for cookie-based auth')

    return {
      success: false,
      data: null as any,
      message: 'Direct sign-in not supported with cookie-based auth',
      errors: [{
        code: 'METHOD_DEPRECATED',
        message: 'Use signInWithHostedUI() for cookie-based authentication'
      }]
    }
  }

  /**
   * Sign out the current user
   * Phase 4.5: Call backend /auth/logout endpoint to clear httpOnly cookies
   *
   * @param customPath - Optional custom logout path with query parameters
   *                     (e.g., "/auth/logout?reason=session_timeout&idle_duration=7200")
   */
  public async signOut(customPath?: string): Promise<ApiResponse<void>> {
    try {
      console.log('🔴 Signing out - calling backend /auth/logout to clear cookies...')

      // Clean up token refresh and idle detection services
      console.log('🧹 Destroying token refresh and idle detection services...')
      tokenRefreshService.destroy()
      idleDetectionService.destroy()

      // Use custom path if provided, otherwise default to /auth/logout
      const logoutPath = customPath || '/auth/logout'
      const url = `${config.authApiBaseUrl}${logoutPath}`
      console.log('🔗 Calling:', url)

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send cookies to backend
        headers: {
          'Content-Type': 'application/json',
        }
      })

      console.log('📡 /auth/logout response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ /auth/logout failed:', response.status, errorText)
        return {
          success: false,
          data: undefined,
          message: `Logout failed: ${response.status}`,
          errors: [{
            code: 'LOGOUT_FAILED',
            message: errorText || `HTTP ${response.status}`
          }]
        }
      }

      console.log('✅ Logged out successfully - cookies cleared by backend')

      return {
        success: true,
        data: undefined,
        message: 'Signed out successfully'
      }
    } catch (error: any) {
      console.error('❌ Logout error:', error)
      return {
        success: false,
        data: undefined,
        message: 'Logout failed',
        errors: [{
          code: error.name || 'LOGOUT_ERROR',
          message: error.message || 'Failed to sign out'
        }]
      }
    }
  }

  /**
   * Sign out with Hosted UI (clears domain session)
   */
  public async signOutWithHostedUI(): Promise<void> {
    // First do the regular signout
    await this.signOut()

    // Then redirect to Cognito logout URL to clear domain session
    const oauthDomain = cognitoConfig.getOAuthDomain()
    if (oauthDomain) {
      const logoutUrl = new URL('/logout', oauthDomain)
      logoutUrl.searchParams.set('client_id', cognitoConfig.getClientId())
      logoutUrl.searchParams.set('logout_uri', config.cognito.oauth?.redirectSignOut || window.location.origin)

      const finalUrl = logoutUrl.toString()
      console.log('🔴 LOGOUT: oauthDomain:', oauthDomain)
      console.log('🔴 LOGOUT: Generated logout URL:', finalUrl)
      console.log('🔴 LOGOUT: Redirecting now...')
      window.location.href = finalUrl
    }
  }

  /**
   * Switch organization context (Phase 3)
   * Calls backend to update cookie with new org context
   *
   * @param orgId - Organization ID to switch to
   * @returns Success indicator (backend refreshes tokens in cookies)
   */
  public async switchOrganization(orgId: string): Promise<ApiResponse<void>> {
    try {
      console.log('🔄 Switching organization to:', orgId)

      const url = `${config.authApiBaseUrl}/me/orgs/${orgId}/switch`
      console.log('🔗 Calling:', url)

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send cookies to backend
        headers: {
          'Content-Type': 'application/json',
        }
      })

      console.log('📡 /me/orgs/switch response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Organization switch failed:', response.status, errorText)
        return {
          success: false,
          data: undefined,
          message: `Organization switch failed: ${response.status}`,
          errors: [{
            code: 'ORG_SWITCH_FAILED',
            message: errorText || `HTTP ${response.status}`
          }]
        }
      }

      console.log('✅ Organization switched successfully - tokens refreshed by backend')

      return {
        success: true,
        data: undefined,
        message: 'Organization switched successfully'
      }
    } catch (error: any) {
      console.error('❌ Organization switch error:', error)
      return {
        success: false,
        data: undefined,
        message: 'Organization switch failed',
        errors: [{
          code: error.name || 'ORG_SWITCH_ERROR',
          message: error.message || 'Failed to switch organization'
        }]
      }
    }
  }

  /**
   * Get user info from backend /me endpoint (cookie-based auth)
   * Phase 2: Reads httpOnly cookies (rp_id_token)
   */
  public async getUserInfo(): Promise<ApiResponse<User>> {
    try {
      console.log('📡 Fetching user info from /me endpoint with cookies...')

      const url = `${config.authApiBaseUrl}/me`
      console.log('🔗 Calling:', url)

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // CRITICAL: Send httpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        }
      })

      console.log('📡 /me response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ /me endpoint failed:', response.status, errorText)
        return {
          success: false,
          data: null as any,
          message: `Failed to fetch user info: ${response.status}`,
          errors: [{
            code: 'GET_USER_INFO_FAILED',
            message: errorText || `HTTP ${response.status}`
          }]
        }
      }

      const wrappedResponse = await response.json()
      console.log('👤 Received user info (wrapped):', wrappedResponse)

      // Extract data from wrapped response format
      const userInfo = wrappedResponse.data
      if (!userInfo) {
        console.error('❌ No user data in wrapped response:', wrappedResponse)
        return {
          success: false,
          data: null as any,
          message: 'No user data in response',
          errors: wrappedResponse.errors || [{ code: 'NO_USER_DATA', message: 'User data missing from response' }]
        }
      }
      console.log('👤 Unwrapped user info:', userInfo)

      // Fetch user's organizations to get proper org names and roles
      let organizations: Organization[] = []
      let currentOrganization: Organization | undefined

      try {
        const orgsUrl = `${config.authApiBaseUrl}/me/orgs`
        const orgsResponse = await fetch(orgsUrl, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        })

        if (orgsResponse.ok) {
          const wrappedResponse = await orgsResponse.json()
          console.log('📋 Fetched organizations (wrapped):', wrappedResponse)

          // Extract data from wrapped response format
          const orgsData = wrappedResponse.data || []
          console.log('📋 Organizations data:', orgsData)

          // Transform org data to Organization type
          organizations = orgsData.map((org: any) => ({
            id: org.id,
            name: org.name,
            role: org.owner ? 'admin' as const : (org.roles?.includes('admin') ? 'admin' as const : 'member' as const),
            permissions: org.owner || org.roles?.includes('admin') ? ['read', 'write', 'admin'] : ['read', 'write']
          }))

          // Find current organization
          currentOrganization = organizations.find(org => org.id === userInfo.orgId)
        } else {
          console.warn('⚠️ Failed to fetch organizations, using fallback')
        }
      } catch (error) {
        console.error('❌ Error fetching organizations:', error)
      }

      // Fallback to basic org info if fetch failed
      if (organizations.length === 0) {
        const fallbackOrg: Organization = {
          id: userInfo.orgId || 'unknown',
          name: userInfo.orgId || 'Unknown Org',
          role: userInfo.isOrgOwner ? 'admin' as const : 'member' as const,
          permissions: userInfo.isOrgOwner ? ['read', 'write', 'admin'] : ['read', 'write']
        }
        organizations = [fallbackOrg]
        currentOrganization = fallbackOrg
      }

      // Transform backend response to User type
      // Use userName from database if available, otherwise extract from email
      const displayName = userInfo.userName || this.extractDisplayNameFromEmail(userInfo.email || '')

      const user: User = {
        id: userInfo.id,
        email: userInfo.email || '',
        name: displayName,
        organizations,
        currentOrganization,
        tokenExpiry: userInfo.tokenExpiry,  // Unix timestamp from ID token 'exp' claim
        // Database profile fields (editable)
        userName: userInfo.userName,
        phoneNumber: userInfo.phoneNumber,
        portraitSetting: userInfo.portraitSetting,
        version: userInfo.version, // Optimistic locking version
        // Global platform roles (rp_admin, rp_support) from token claims
        globalRoles: userInfo.roles || [],
        // User's subscription plan from /me endpoint
        plan: userInfo.plan,
        // EULA acceptance - null means never accepted
        eulaAcceptedVersion: userInfo.eulaAcceptedVersion ?? null
      }

      console.log('✅ Transformed user info:', user)

      // Initialize stack selection based on user email
      stackService.initializeStackForUser(user.email)

      // NOTE: Token refresh and idle detection initialization moved to useCurrentUser hook
      // to ensure it runs on all authentication paths (login, refresh, etc.)

      return {
        success: true,
        data: user,
        message: 'User info retrieved from cookies'
      }
    } catch (error: any) {
      console.error('❌ getUserInfo error:', error)
      return {
        success: false,
        data: null as any,
        message: 'Failed to fetch user info',
        errors: [{
          code: error.name || 'GET_USER_INFO_ERROR',
          message: error.message || 'Failed to retrieve user information'
        }]
      }
    }
  }

  /**
   * Get current authenticated user
   * Phase 4.4: Now uses /me endpoint with cookie-based auth
   */
  public async getCurrentUser(): Promise<ApiResponse<User | null>> {
    try {
      const result = await this.getUserInfo()

      if (!result.success) {
        // If getUserInfo fails, user is not authenticated
        return {
          success: true,
          data: null,
          message: 'No user authenticated'
        }
      }

      return result
    } catch (error: any) {
      console.error('Get current user error:', error)

      return {
        success: true,
        data: null,
        message: 'No user authenticated'
      }
    }
  }

  /**
   * Mock authentication for development (when Cognito is not configured)
   */
  public async mockSignIn(email: string): Promise<ApiResponse<User>> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Simulate random failure for realistic testing
    if (Math.random() > 0.8) {
      return {
        success: false,
        data: null as any,
        message: 'Mock authentication failed',
        errors: [{
          code: 'MOCK_AUTH_FAILURE',
          message: 'Simulated authentication failure for testing error handling'
        }]
      }
    }

    // Generate user name from email
    const nameFromEmail = email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    
    // Determine organizations based on email domain or specific emails
    let organizations: Organization[] = [
      {
        id: 'org-2',
        name: 'Space Kitty Demo',
        role: 'member' as const,
        permissions: ['read', 'write']
      }
    ]
    
    if (email.includes('@rocketpricing.com') || email === 'admin@test.com') {
      organizations = [
        {
          id: 'org-1',
          name: 'Rocket Pricing',
          role: 'admin' as const,
          permissions: ['read', 'write', 'admin']
        },
        {
          id: 'org-2',
          name: 'Space Kitty Demo',
          role: 'member' as const,
          permissions: ['read', 'write']
        }
      ]
    }

    const mockUser: User = {
      id: `mock-user-${Date.now()}`,
      email,
      name: nameFromEmail,
      organizations,
      currentOrganization: organizations[0]
    }

    return {
      success: true,
      data: mockUser,
      message: 'Mock authentication successful'
    }
  }

  /**
   * Accept EULA version
   * Updates user's accepted EULA version in database
   */
  public async acceptEula(version: number): Promise<ApiResponse<{ accepted: boolean; version: number }>> {
    try {
      console.log('📝 Accepting EULA version:', version)

      const url = `${config.authApiBaseUrl}/me/eula/accept`
      console.log('🔗 Calling:', url)

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send cookies to backend
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ version })
      })

      console.log('📡 /me/eula/accept response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ EULA acceptance failed:', response.status, errorText)
        return {
          success: false,
          data: undefined as any,
          message: `EULA acceptance failed: ${response.status}`,
          errors: [{
            code: 'EULA_ACCEPT_FAILED',
            message: errorText || `HTTP ${response.status}`
          }]
        }
      }

      const wrappedResponse = await response.json()
      console.log('✅ EULA accepted:', wrappedResponse)

      return {
        success: true,
        data: wrappedResponse.data,
        message: 'EULA accepted successfully'
      }
    } catch (error: any) {
      console.error('❌ EULA acceptance error:', error)
      return {
        success: false,
        data: undefined as any,
        message: 'EULA acceptance failed',
        errors: [{
          code: error.name || 'EULA_ACCEPT_ERROR',
          message: error.message || 'Failed to accept EULA'
        }]
      }
    }
  }

  /**
   * Refresh tokens to get updated claims (e.g., after EULA acceptance)
   * Calls backend to refresh the ID token cookie with updated claims
   */
  public async refreshTokens(): Promise<ApiResponse<void>> {
    try {
      console.log('🔄 Refreshing tokens...')

      const url = `${config.authApiBaseUrl}/auth/refresh`
      console.log('🔗 Calling:', url)

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send cookies to backend
        headers: {
          'Content-Type': 'application/json',
        }
      })

      console.log('📡 /auth/refresh response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Token refresh failed:', response.status, errorText)
        return {
          success: false,
          data: undefined,
          message: `Token refresh failed: ${response.status}`,
          errors: [{
            code: 'TOKEN_REFRESH_FAILED',
            message: errorText || `HTTP ${response.status}`
          }]
        }
      }

      console.log('✅ Tokens refreshed successfully')

      return {
        success: true,
        data: undefined,
        message: 'Tokens refreshed successfully'
      }
    } catch (error: any) {
      console.error('❌ Token refresh error:', error)
      return {
        success: false,
        data: undefined,
        message: 'Token refresh failed',
        errors: [{
          code: error.name || 'TOKEN_REFRESH_ERROR',
          message: error.message || 'Failed to refresh tokens'
        }]
      }
    }
  }

  // Private helper methods

  private extractDisplayNameFromEmail(email: string): string {
    if (!email) return 'User'

    const emailPrefix = email.split('@')[0]

    // Handle different email formats to extract first name
    if (emailPrefix.includes('.')) {
      // john.doe@example.com → John
      const firstName = emailPrefix.split('.')[0]
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    } else if (emailPrefix.includes('_')) {
      // john_doe@example.com → John
      const firstName = emailPrefix.split('_')[0]
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    } else if (emailPrefix.includes('-')) {
      // john-doe@example.com → John
      const firstName = emailPrefix.split('-')[0]
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    } else {
      // jsalcedo@example.com → Jsalcedo
      return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).toLowerCase()
    }
  }

  private generateState(): string {
    // Generate a random state string for CSRF protection
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
  }
}

// Export singleton instance
export const authService = AuthService.getInstance()
export default authService